import React, { useState } from 'react';
import { startRegistration } from '@simplewebauthn/browser';
import type { RegistrationResponseJSON } from '@simplewebauthn/browser';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, UserPlus, AlertCircle, CheckCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LoadingOverlay } from '@/components/ui/loading';
import { handleWebAuthnError, handleAPIError, showErrorToast, showSuccessToast } from '@/utils/error-handler';
import { toast } from 'sonner';

interface RegisterFormProps {
  onSuccess?: (user: { id: string; username: string; isAdmin?: boolean }, token: string) => void;
  onError?: (error: string) => void;
}

export function RegisterForm({ onSuccess, onError }: RegisterFormProps) {
  const [username, setUsername] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const validateUsername = (value: string): string[] => {
    const errors: string[] = [];
    
    if (!value.trim()) {
      errors.push('Username is required');
    } else {
      if (value.length < 3) {
        errors.push('Username must be at least 3 characters long');
      }
      if (value.length > 20) {
        errors.push('Username must be less than 20 characters');
      }
      if (!/^[a-zA-Z0-9_-]+$/.test(value)) {
        errors.push('Username can only contain letters, numbers, hyphens, and underscores');
      }
    }
    
    return errors;
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUsername(value);
    setValidationErrors(validateUsername(value));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const errors = validateUsername(username);
    if (errors.length > 0) {
      setValidationErrors(errors);
      setError(errors[0] || 'Validation error');
      return;
    }

    setIsLoading(true);
    setError(null);

    // Show loading toast
    const loadingToast = toast.loading('Starting registration...');

    try {
      // Step 1: Begin registration
      const beginResponse = await fetch('/api/auth/register/begin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username: username.trim() }),
      });

      if (!beginResponse.ok) {
        const errorData = await beginResponse.json();
        const apiError = handleAPIError(beginResponse, errorData);
        throw apiError;
      }

      const { options, challengeId } = await beginResponse.json();
      
      // Update loading message
      toast.loading('Please use your authenticator...', { id: loadingToast });

      // Step 2: Start WebAuthn registration
      let registrationResponse: RegistrationResponseJSON;
      try {
        registrationResponse = await startRegistration({
          ...options,
          // Ensure proper user verification
          authenticatorSelection: {
            ...options.authenticatorSelection,
            userVerification: 'preferred',
          },
        });
      } catch (webauthnError: any) {
        const error = handleWebAuthnError(webauthnError);
        throw error;
      }

      // Update loading message
      toast.loading('Completing registration...', { id: loadingToast });

      // Step 3: Complete registration
      const completeResponse = await fetch('/api/auth/register/complete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          credential: registrationResponse,
          challengeId,
          username: username.trim(),
        }),
      });

      if (!completeResponse.ok) {
        const errorData = await completeResponse.json();
        const apiError = handleAPIError(completeResponse, errorData);
        throw apiError;
      }

      const { user, token } = await completeResponse.json();

      // Store token in localStorage for session persistence
      localStorage.setItem('auth-token', token);

      // Dismiss loading toast and show success
      toast.dismiss(loadingToast);
      showSuccessToast('Registration successful!', `Welcome, ${user.username}!`);

      // Call success callback
      onSuccess?.(user, token);

    } catch (err: any) {
      // Dismiss loading toast
      toast.dismiss(loadingToast);
      
      const errorMessage = err.message || 'Registration failed. Please try again.';
      setError(errorMessage);
      showErrorToast(err);
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LoadingOverlay isLoading={isLoading} text="Processing registration...">
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <CardTitle className="flex items-center justify-center gap-2">
            <UserPlus className="h-5 w-5" />
            Create Account
          </CardTitle>
          <CardDescription>
            Register with just your username and biometric authentication
          </CardDescription>
        </CardHeader>
        <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              type="text"
              placeholder="Enter your username"
              value={username}
              onChange={handleUsernameChange}
              disabled={isLoading}
              autoComplete="username"
              autoFocus
              className={`w-full ${validationErrors.length > 0 ? 'border-destructive' : ''}`}
            />
            {validationErrors.length > 0 && (
              <div className="space-y-1">
                {validationErrors.map((error, index) => (
                  <p key={index} className="text-xs text-destructive">
                    {error}
                  </p>
                ))}
              </div>
            )}
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button
            type="submit"
            disabled={isLoading || !username.trim() || validationErrors.length > 0}
            className="w-full"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating Account...
              </>
            ) : (
              <>
                <UserPlus className="mr-2 h-4 w-4" />
                Register
              </>
            )}
          </Button>

          <div className="text-xs text-muted-foreground text-center space-y-1">
            <p>Registration uses your device's built-in security features</p>
            <p>No passwords required!</p>
          </div>
        </form>
      </CardContent>
    </Card>
    </LoadingOverlay>
  );
}